import { useCallback, useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import Lenis from 'lenis'

export const useScrollbar = (lenis?: Lenis) => {
  const [isDragged, setIsDragged] = useState(false)
  const [needsScrollbar, setNeedsScrollbar] = useState(false)
  const track = useRef<HTMLDivElement>(null!)
  const thumb = useRef<HTMLDivElement>(null!)
  const thumbPointerOffset = useRef(0)
  const thumbHeight = useRef(0)
  const trackHeight = useRef(0)
  const whatcher = useRef({
    prevLimit: 0,
    currLimit: 0
  })

  const resize = useCallback(() => {
    if (!lenis) return

    const height = track.current.offsetHeight
    const wrapperHeight = lenis.dimensions.height
    const contentHeight = lenis.dimensions.scrollHeight
    const ratio = Math.min(1, wrapperHeight / contentHeight)
    thumbHeight.current = height * ratio
    trackHeight.current = height

    gsap.set(thumb.current, { height: thumbHeight.current })

    setNeedsScrollbar(ratio < 1)
  }, [lenis])

  useEffect(() => {
    if (!lenis) return

    const update = (lenis: Lenis) => {
      if (isDragged) return
      thumb.current &&
        gsap.set(thumb.current, {
          y: lenis.progress * (trackHeight.current - thumbHeight.current)
        })
    }

    lenis.on('scroll', update)

    return () => {
      lenis.off('scroll', update)
    }
  }, [lenis, isDragged])

  useEffect(() => {
    if (!lenis || !isDragged) return

    const handleMouseDown = (event: MouseEvent) => {
      event.preventDefault()

      const thumbPosition = gsap.getProperty(thumb.current, 'y') as number
      thumbPointerOffset.current = event.clientY - thumbPosition
    }

    const handleMouseMove = (event: MouseEvent) => {
      event.preventDefault()

      const thumbMaxPosition = trackHeight.current - thumbHeight.current
      const thumbPointerPosition = event.clientY - thumbPointerOffset.current
      const thumbPosition = gsap.utils.clamp(
        0,
        thumbMaxPosition,
        thumbPointerPosition
      )

      const scrollPosition = gsap.utils.mapRange(
        0,
        thumbMaxPosition,
        0,
        lenis.limit,
        thumbPosition
      )

      gsap.set(thumb.current, { y: thumbPosition })

      lenis.scrollTo(scrollPosition, { immediate: true })
    }

    const handleMouseUp = () => setIsDragged(false)

    window.addEventListener('mousedown', handleMouseDown, {
      passive: false
    })
    window.addEventListener('mousemove', handleMouseMove, {
      passive: false
    })
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [lenis, isDragged])

  useEffect(() => {
    if (!lenis) return

    const currentThumb = thumb.current

    const handleMouseDown = () => setIsDragged(true)

    currentThumb?.addEventListener('mousedown', handleMouseDown)

    return () => {
      currentThumb?.removeEventListener('mousedown', handleMouseDown)
    }
  }, [lenis])

  useEffect(() => {
    if (!lenis) return

    const tick = () => {
      whatcher.current.prevLimit = whatcher.current.currLimit
      whatcher.current.currLimit = lenis.limit
      whatcher.current.prevLimit > 0 &&
        whatcher.current.currLimit !== whatcher.current.prevLimit &&
        resize()
    }

    gsap.ticker.add(tick)

    return () => {
      gsap.ticker.remove(tick)
    }
  }, [lenis, resize])

  useEffect(() => {
    if (!lenis) return

    window.addEventListener('resize', resize)

    resize()

    return () => {
      window.removeEventListener('resize', resize)
    }
  }, [lenis, resize])

  return [track, thumb, needsScrollbar, resize] as const
}
